import axios from 'axios';

import { getAxiosInstance } from './axios';

import { AGMS_BASE_URL, AGMS_SERVER, API_URL, CONNECTION_TIMEOUT, REQUST_TIMEOUT } from '../util/environment';

import { AuthResponse } from '../interface/response/AuthResponse';
import { RoleAndPermissionsResponse } from '../interface/response/RoleAndPermissionsResponse';
import { DeviceListResponse } from '../interface/response/device-list/DeviceListResponse';
import { DeviceResponse } from '../interface/response/device/DeviceResponse';
import { DeviceLogsResponse } from '../interface/response/device-logs/DeviceLogsResponse';
import { DeviceLogsRequest } from '../interface/request/DeviceLogsRequest';
import { DeviceStatusResponse } from '../interface/response/device-status/DeviceStatusResponse';
import { ShellVersionsResponse } from '../interface/response/ShellVersionsResponse';
import { UpdateShellRequest } from '../interface/request/UpdateShellRequest';

import { ColumnName } from '../enum/health-monitor/ColumnName';
import { AutocompleteResponse } from '../interface/response/AutocompleteResponse';
import { getAxiosInstanceAGMS } from './axios-agms';
import { GeoFiltersRequest } from '../interface/request/GeoFiltersRequest';
import { StatsDataRequest } from '../interface/request/StatsDataRequest';
import { Export } from '../enum/Export';
import { ApplicationConfig, ConfigResponse } from '../interface/response/ApplicationConfig';
import { CashRegisterDataRequest } from '../interface/request/CashRegisterRequest';
import { CashRegisterDataResponse } from '../interface/response/CashRegisterDataResponse';
import { StatsExport } from '../interface/request/StatsExport';
import { CashRegisterDetailsRequest } from '../interface/request/CashRegisterDetailsRequest';
import { CashRegisterDetailsResponse } from '../interface/response/CashRegisterDetailsResponse';
import { OperatorDetailsRequest } from '../interface/request/OperatorDetailsRequest';
import { OperatorDetailsResponse } from '../interface/response/OperatorDetailsResponse';
import { StatsResponse } from '../interface/stats/StatsResponse';

export const ApiConsumer = {
  async authenticate(
    username: string,
    password: string,
  ): Promise<{
    access_token: string;
    token_type: string;
    userName: string;
    '.expires': string;
  }> {
    let response;

    const params = new URLSearchParams();
    params.append('username', username);
    params.append('password', password);
    params.append('grant_type', 'password');

    const encodedString = params.toString();

    response = await axios.post(`${AGMS_BASE_URL}/${AGMS_SERVER}/Token`, encodedString, {
      signal: AbortSignal.timeout(CONNECTION_TIMEOUT),
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8',
      },
      timeout: REQUST_TIMEOUT,
    });

    return response.data;
  },

  async authenticateOnHealthMonitor(token: string): Promise<AuthResponse> {
    let response;

    response = await axios.post(`${API_URL}/authentication?token=${token}`);

    return response.data;
  },

  async refreshAccessToken(): Promise<AuthResponse> {
    let response;

    response = await getAxiosInstance().get(`${API_URL}/accessToken`);

    return response.data;
  },

  async getDeviceList(
    page: number = 0,
    size: number = 20,
    desc: boolean = true,
    sortColumn: ColumnName = ColumnName.NAME,
    searchColumn: ColumnName = ColumnName.NAME,
    search: string = '',
    warnings: string = '',
    status: string = '',
  ): Promise<DeviceListResponse> {
    let response;

    response = await getAxiosInstance().post('/devices', {
      page: page,
      size: size,
      descending: desc,
      sortColumn: sortColumn,
      searchColumn: searchColumn,
      search: search,
      warnings: warnings,
      status: status,
    });

    return {
      ...response.data,
      totalWarningsByType: JSON.stringify(response.data.totalWarningsByType),
    };
  },

  async getAutocompleteList(search: string, column: ColumnName, maxSize: number): Promise<AutocompleteResponse> {
    let response;

    response = await getAxiosInstance().post('/autocomplete', {
      search: search,
      column: column,
      maxSize: maxSize,
    });

    return response.data;
  },

  async getDeviceDetails(deviceId: string): Promise<DeviceResponse> {
    let response;

    response = await getAxiosInstance().get(`/deviceInfo/?deviceUserId=${deviceId}`);

    return response.data;
  },

  async getDeviceLogs(deviceLogsRequest: DeviceLogsRequest): Promise<DeviceLogsResponse[]> {
    let response;

    response = await getAxiosInstance().post('/deviceLogs', deviceLogsRequest);

    return response.data;
  },

  async getRoleAndPermissions(): Promise<RoleAndPermissionsResponse> {
    let response;

    response = await getAxiosInstance().get('api/UserAuthentication/Permission');

    return response.data;
  },

  async getRemoteAccess(deviceId: string | null): Promise<string> {
    let response;

    response = await getAxiosInstance().get(`/remoteAccess/?deviceId=${deviceId}`);

    return response.data;
  },

  async getDeviceStatus(deviceId: string | undefined): Promise<DeviceStatusResponse> {
    let response;

    response = await getAxiosInstance().get(`/status/?deviceId=${deviceId}`);

    return response.data;
  },

  async getShellVersions(deviceUserId: string): Promise<ShellVersionsResponse[]> {
    let response;

    response = await getAxiosInstance().get(`/shell/versions?deviceUserId=${deviceUserId}`);

    return response.data;
  },

  async getTimezones(): Promise<string[]> {
    let response;
    response = await getAxiosInstanceAGMS().get(`DropDownList/GetTimezones`);
    return response.data;
  },

  async export(payload: StatsExport): Promise<any> {
    let response;

    response = await getAxiosInstanceAGMS().post('api/Stats/Export', payload, {
      responseType: Export.BLOB,
    });

    const blob = new Blob([response.data], {
      type: Export.EXCEL_TYPE,
    });

    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = Export.EXPORT;
    document.body.appendChild(a);
    a.click();
    a.remove();

    window.URL.revokeObjectURL(url);
  },

  async getStats(payload: StatsDataRequest): Promise<StatsResponse> {
    let response;

    response = await getAxiosInstanceAGMS().post('/api/Stats', payload);

    return response.data;
  },

  async getCashRegisterData(payload: CashRegisterDataRequest): Promise<CashRegisterDataResponse> {
    let response;

    response = await getAxiosInstanceAGMS().post('/api/CashRegister', payload);

    return response.data;
  },

  async getCashRegisterDetails(payload: CashRegisterDetailsRequest): Promise<CashRegisterDetailsResponse> {
    let response;

    response = await getAxiosInstanceAGMS().post('/api/CashRegister/Details', payload);

    return response.data;
  },

  async getOperatorDetails(payload: OperatorDetailsRequest): Promise<OperatorDetailsResponse> {
    let response;

    response = await getAxiosInstanceAGMS().post('/api/CashRegister/OperatorDetails', payload);

    return response.data;
  },

  async getGeoFilters(payload: GeoFiltersRequest): Promise<any> {
    let response;

    response = await getAxiosInstanceAGMS().post('/api/Geo/Filter', payload);

    return response.data;
  },

  async getGamesFilter(payload: {}): Promise<any> {
    let response;

    response = await getAxiosInstanceAGMS().get('api/Filter/Games', {
      params: payload,
    });

    return response.data;
  },

  async getConfig(): Promise<ConfigResponse> {
    const response = await getAxiosInstanceAGMS().get('api/Geo/Config');

    return response.data as ConfigResponse;
  },

  async updateConfig(payload: ApplicationConfig): Promise<ConfigResponse> {
    const response = await getAxiosInstanceAGMS().post('api/Geo/Config', payload);

    return response.data as ConfigResponse;
  },

  async setShellVersions(updateShellRequest: UpdateShellRequest): Promise<any> {
    let response;

    response = await getAxiosInstance().post(`/shell/versions`, updateShellRequest);

    return response.data;
  },

  async restartShell(deviceId: string): Promise<any> {
    let response;

    response = await getAxiosInstance().get(`/restart?deviceUserId=${deviceId}`);

    return response.data;
  },
};
